"use client";

import { useState, useEffect, Suspense } from "react";
import Link from "next/link";
import { useRouter, useSearchParams } from "next/navigation";
import { useAuth } from "@/hooks/use-auth";

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.47 2.09 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
    </svg>
  );
}

const ERROR_MESSAGES: Record<string, string> = {
  session_expired: "Your session expired — please sign in again.",
  oauth_error: "Sign-in failed. Please try again.",
  access_denied: "Access was denied. Please try again.",
  link_expired: "This sign-in link has expired. Please request a new one.",
  link_invalid: "This sign-in link is invalid. Please request a new one.",
  link_used: "This sign-in link has already been used. Please request a new one.",
};

function WebflowIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 256 256" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M192.273 0H63.727C28.528 0 0 28.528 0 63.727v128.546C0 227.472 28.528 256 63.727 256h128.546C227.472 256 256 227.472 256 192.273V63.727C256 28.528 227.472 0 192.273 0Z" fill="#146EF5" />
      <path d="M173.426 80s-22.03 69.516-23.098 73.017c-.427-2.989-13.397-73.017-13.397-73.017h-26.386s-13.824 70.666-14.25 73.351C95.226 150.37 72.574 80 72.574 80H46l33.63 96h27.744l13.866-66.835L135.106 176h27.744L196.48 80h-23.054Z" fill="#fff" />
    </svg>
  );
}

function MagicLinkIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <path d="m22 7-10 5L2 7" />
    </svg>
  );
}

type AuthState = "idle" | "sending" | "sent" | "resending";

function LoginContent() {
  const router = useRouter();
  const params = useSearchParams();
  const { user, loading } = useAuth({ skipRedirect: true });

  const errorKey = params?.get("error") ?? "";
  const redirect = params?.get("redirect") ?? "";
  const successMsg = params?.get("success") === "magic_link" ? "Sign-in link verified — loading your dashboard…" : null;

  const errorMsg = errorKey in ERROR_MESSAGES
    ? ERROR_MESSAGES[errorKey]
    : errorKey
      ? "Something went wrong — please sign in again."
      : null;

  const [mounted, setMounted] = useState(false);
  useEffect(() => { setMounted(true); }, []);

  const [email, setEmail] = useState("");
  const [authState, setAuthState] = useState<AuthState>("idle");
  const [formError, setFormError] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(0);

  useEffect(() => {
    if (!loading && user) {
      router.replace(redirect && redirect.startsWith("/") ? redirect : "/dashboard");
    }
  }, [user, loading, redirect, router]);

  useEffect(() => {
    if (countdown <= 0) return;
    const timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [countdown]);

  async function handleMagicLink(e: React.FormEvent, resend = false) {
    e.preventDefault();
    if (!email.trim()) { setFormError("Please enter your email address."); return; }
    setFormError(null);
    setAuthState(resend ? "resending" : "sending");

    try {
      const destination = redirect && redirect.startsWith("/") ? redirect : "/dashboard";
      const res = await fetch("/api/auth/magic-link", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), redirect: destination }),
      });
      const body = await res.json() as { ok?: boolean; redirect?: string; error?: string };
      if (!res.ok) {
        throw new Error(body.error ?? "Failed to sign in. Please try again.");
      }
      // Non-blocking: redirect immediately
      if (body.ok && body.redirect) {
        router.replace(body.redirect);
        return;
      }
      // Fallback
      router.replace(destination);
    } catch (err) {
      setFormError((err as Error).message);
      setAuthState("idle");
    }
  }

  const webflowHref = `/api/auth/webflow${redirect ? `?redirect=${encodeURIComponent(redirect)}` : ""}`;

  if (!mounted || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-sm text-muted-foreground">Loading…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 px-4 py-12">
      <div className="w-full max-w-[480px]">
        <div className="flex justify-center mb-10">
          <Link href="/">
            <picture>
              <source srcSet="/images/legible-logo.webp" type="image/webp" />
              <img src="/images/legible-logo.png" alt="Legible" className="h-10 w-auto" width={138} height={40} />
            </picture>
          </Link>
        </div>

        <div className="bg-white border border-border rounded-2xl shadow-sm px-8 py-10">
          <h1 className="text-2xl font-bold text-foreground tracking-tight mb-2 text-center">
            Sign in to Legible
          </h1>
          <p className="text-sm text-muted-foreground text-center mb-8">
            Connect your site and make your content AI-readable.
          </p>

          {successMsg && (
            <div className="mb-5 px-4 py-3 rounded-lg bg-green-50 border border-green-200 text-sm text-green-700">
              {successMsg}
            </div>
          )}

          {(errorMsg || formError) && (
            <div className="mb-5 px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">
              {errorMsg ?? formError}
            </div>
          )}

          {authState === "sent" || authState === "resending" ? (
            <div className="text-center">
              <div className="w-16 h-16 bg-green-50 rounded-2xl flex items-center justify-center mx-auto mb-5">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" className="text-green-600">
                  <rect x="2" y="4" width="20" height="16" rx="2" />
                  <path d="m22 7-10 5L2 7" />
                </svg>
              </div>
              <h2 className="text-lg font-bold text-foreground mb-2">Check your inbox</h2>
              <p className="text-sm text-muted-foreground mb-6">
                We sent a sign-in link to <strong className="text-foreground">{email}</strong>.
              </p>
              <button
                onClick={(e) => { void handleMagicLink(e, true); }}
                disabled={countdown > 0 || authState === "resending"}
                className="w-full mb-3 px-5 py-2.5 rounded-xl border border-border text-sm font-medium text-foreground hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {authState === "resending" ? "Sending…" : countdown > 0 ? `Resend in ${countdown}s` : "Resend email"}
              </button>
              <button onClick={() => { setAuthState("idle"); setEmail(""); }} className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                Use a different email
              </button>
            </div>
          ) : (
            <>
              <a
                href={`/api/auth/google${redirect ? `?redirect=${encodeURIComponent(redirect)}` : ""}`}
                className="flex items-center justify-center gap-2.5 w-full px-5 py-3.5 rounded-xl bg-white text-gray-800 font-semibold text-sm hover:bg-gray-50 border border-gray-200 transition-colors mb-3 shadow-sm"
              >
                <GoogleIcon />
                Continue with Google
              </a>

              <a
                href={webflowHref}
                className="flex items-center justify-center gap-2.5 w-full px-5 py-3.5 rounded-xl bg-[#146EF5] text-white font-semibold text-sm hover:bg-[#0f5fd8] transition-colors mb-4"
              >
                <WebflowIcon />
                Continue with Webflow
              </a>

              <div className="relative mb-4">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-border" />
                </div>
                <div className="relative flex justify-center">
                  <span className="bg-white px-3 text-xs text-muted-foreground">or continue with email</span>
                </div>
              </div>

              <form onSubmit={(e) => { void handleMagicLink(e); }} className="space-y-3">
                <input
                  type="email"
                  autoComplete="email"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="w-full px-4 py-3 rounded-xl border border-border text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
                />
                <button
                  type="submit"
                  disabled={authState === "sending"}
                  className="w-full flex items-center justify-center gap-2 px-5 py-3.5 rounded-xl bg-foreground text-background font-semibold text-sm hover:bg-foreground/90 transition-colors disabled:opacity-60"
                >
                  <MagicLinkIcon />
                  {authState === "sending" ? "Sending link…" : "Continue with email"}
                </button>
              </form>

              <p className="mt-6 text-xs text-muted-foreground text-center">
                By continuing, you agree to our{" "}
                <Link href="/terms/" className="underline underline-offset-2 hover:text-foreground">Terms</Link>
                {" "}and{" "}
                <Link href="/privacy/" className="underline underline-offset-2 hover:text-foreground">Privacy Policy</Link>.
              </p>
            </>
          )}
        </div>

        <p className="mt-6 text-center text-sm text-muted-foreground">
          Don&apos;t have an account?{" "}
          <Link href="/signup?plan=pro&trial=true" className="text-primary font-medium hover:underline">
            Get started free
          </Link>
        </p>
      </div>
    </div>
  );
}

export default function LoginPage() {
  return (
    <Suspense fallback={
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-sm text-muted-foreground">Loading…</div>
      </div>
    }>
      <LoginContent />
    </Suspense>
  );
}
